import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Flame, Droplet, ThermometerSun, Users } from 'lucide-react';

interface TemperatureData {
  saunaTemp: number;
  plungeTemp: number;
  timestamp: string;
  userId: string;
}

interface TemperatureInputProps {
  locationId: string;
  locationName: string;
  onTemperatureSubmit?: (data: { saunaTemp: number; plungeTemp: number }) => void;
}

const TemperatureInput: React.FC<TemperatureInputProps> = ({
  locationId,
  locationName,
  onTemperatureSubmit
}) => {
  const [saunaTemp, setSaunaTemp] = useState<string>('');
  const [plungeTemp, setPlungeTemp] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Mock community averages - in real app this would come from API
  const communityAverages = {
    saunaTemp: 82.3,
    plungeTemp: 9.1,
    totalReadings: 24
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const saunaTempNum = parseFloat(saunaTemp);
    const plungeTempNum = parseFloat(plungeTemp);

    if (isNaN(saunaTempNum) || isNaN(plungeTempNum)) {
      alert('Please enter valid temperatures');
      return;
    }

    if (saunaTempNum < 50 || saunaTempNum > 120) {
      alert('Sauna temperature should be between 50°C and 120°C');
      return;
    }

    if (plungeTempNum < -5 || plungeTempNum > 25) {
      alert('Plunge temperature should be between -5°C and 25°C');
      return;
    }

    setIsLoading(true);

    try {
      // Mock API call - in real app this would submit to backend
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const temperatureData: TemperatureData = {
        saunaTemp: saunaTempNum,
        plungeTemp: plungeTempNum,
        timestamp: new Date().toISOString(),
        userId: 'current-user-id' // In real app this would come from auth context
      };

      // TODO: Submit to API
      console.log('Temperature data submitted:', temperatureData);

      if (onTemperatureSubmit) {
        onTemperatureSubmit({ saunaTemp: saunaTempNum, plungeTemp: plungeTempNum });
      }

      setIsSubmitted(true);
      setSaunaTemp('');
      setPlungeTemp('');
    } catch (error) {
      console.error('Error submitting temperature data:', error);
      alert('Failed to submit temperature data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setIsSubmitted(false);
    setSaunaTemp('');
    setPlungeTemp('');
  };

  return (
    <Card className="border-sauna-orange/20 bg-gradient-to-br from-sauna-orange/5 to-sauna-blue/5">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2 text-sauna-navy">
          <ThermometerSun className="h-5 w-5 text-sauna-orange" />
          <span>🧪 BONUS: Invite Readings & Contributions</span>
        </CardTitle>
        <p className="text-sm text-gray-600">
          What was the sauna or plunge temperature when you visited? Help others know what to expect! (We'll average temps over time.)
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Community Averages Display */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4 border border-sauna-orange/20">
            <div className="flex items-center space-x-2 mb-2">
              <Flame className="h-4 w-4 text-sauna-orange" />
              <span className="text-sm font-medium text-sauna-navy">Avg Sauna Temp</span>
            </div>
            <div className="text-2xl font-bold text-sauna-orange">
              {communityAverages.saunaTemp}°C
            </div>
            <div className="text-xs text-gray-500">
              Based on {communityAverages.totalReadings} readings
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-4 border border-sauna-blue/20">
            <div className="flex items-center space-x-2 mb-2">
              <Droplet className="h-4 w-4 text-sauna-blue" />
              <span className="text-sm font-medium text-sauna-navy">Avg Plunge Temp</span>
            </div>
            <div className="text-2xl font-bold text-sauna-blue">
              {communityAverages.plungeTemp}°C
            </div>
            <div className="text-xs text-gray-500">
              Based on {communityAverages.totalReadings} readings
            </div>
          </div>
        </div>

        {!isSubmitted ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sauna-temp" className="flex items-center space-x-2">
                  <Flame className="h-4 w-4 text-sauna-orange" />
                  <span>🔥 Sauna Temp</span>
                </Label>
                <div className="relative">
                  <Input
                    id="sauna-temp"
                    type="number"
                    value={saunaTemp}
                    onChange={(e) => setSaunaTemp(e.target.value)}
                    placeholder="Enter temperature"
                    className="pr-12"
                    min="50"
                    max="120"
                    step="0.1"
                    required
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                    °C
                  </span>
                </div>
                <p className="text-xs text-gray-500">Range: 50°C - 120°C</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="plunge-temp" className="flex items-center space-x-2">
                  <Droplet className="h-4 w-4 text-sauna-blue" />
                  <span>❄️ Plunge Temp</span>
                </Label>
                <div className="relative">
                  <Input
                    id="plunge-temp"
                    type="number"
                    value={plungeTemp}
                    onChange={(e) => setPlungeTemp(e.target.value)}
                    placeholder="Enter temperature"
                    className="pr-12"
                    min="-5"
                    max="25"
                    step="0.1"
                    required
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                    °C
                  </span>
                </div>
                <p className="text-xs text-gray-500">Range: -5°C - 25°C</p>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-sauna-orange hover:bg-sauna-orange/90 text-white"
              disabled={isLoading || !saunaTemp || !plungeTemp}
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Submitting...</span>
                </div>
              ) : (
                <span>Submit Temperature Reading</span>
              )}
            </Button>
          </form>
        ) : (
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center space-x-2 text-sauna-green">
              <div className="w-8 h-8 rounded-full bg-sauna-green/20 flex items-center justify-center">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="font-medium">Thank you for your contribution!</span>
            </div>
            <p className="text-sm text-gray-600">
              Your temperature reading has been recorded and will help improve the community averages.
            </p>
            <Button
              variant="outline"
              onClick={handleReset}
              className="border-sauna-orange text-sauna-orange hover:bg-sauna-orange hover:text-white"
            >
              Submit Another Reading
            </Button>
          </div>
        )}

        <div className="text-xs text-gray-500 text-center">
          <Users className="h-3 w-3 inline mr-1" />
          Community-driven data helps everyone have better expectations
        </div>
      </CardContent>
    </Card>
  );
};

export default TemperatureInput; 